import { useMemo } from 'react';
import useSWR, { useSWRConfig, Fetcher } from 'swr';

import { api } from '../../api/api';
import { SWR_KEYS } from './swrKeys';

import { Customer } from './models';
import { CachedResult } from './interfaces';

// Define the shape of your filter params
export interface CustomerFilters {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export function useCustomers(filters?: CustomerFilters) {
  const { mutate: globalMutate } = useSWRConfig();

  // SWR Key determined based on whether filters are present.
  const swrKey = useMemo(() => {
    // If no filters are passed, use the base key
    if (!filters || (!filters.firstName && !filters.lastName && !filters.email)) {
      return SWR_KEYS.CUSTOMERS;
    }

    // If filters exist, construct the query string
    const params = new URLSearchParams();
    if (filters.firstName) params.append('firstName', filters.firstName);
    if (filters.lastName)  params.append('lastName', filters.lastName);
    if (filters.email)     params.append('email', filters.email);

    return `${SWR_KEYS.CUSTOMERS}/filter?${params.toString()}`;
  }, [filters]);

  // Read Logic
  const customerFetcher: Fetcher<CachedResult<Customer[]>, string> = async (url: string) => {
    const customers: Customer[]= await api.get(url).then((response) => {return response.data;});
    return {
        data: customers,
        lastUpdated: Date.now()
    };
  }
  const {
    data: customerData, 
    mutate: mutateCustomer, 
    error, 
    isLoading 
  } = useSWR<CachedResult<Customer[]>>(swrKey, customerFetcher);

  // Write Logic (Action)
  const createCustomer = async (inputCustomerData: Partial<Customer>) => {
    // Call the server to create the customer
    const response = await api.post(SWR_KEYS.CUSTOMERS, inputCustomerData);
    const newId = response.data;

    // Update the local object witht the returned Id
    const dateTimeString = new Date().toISOString();
    const newCustomer = { 
      ...inputCustomerData, 
      _id: newId,
      createdAt: dateTimeString,
      updatedAt: dateTimeString,
    } as Customer;
    
    // Update async the cache (SWR)
    globalMutate(
        SWR_KEYS.CUSTOMERS, 
        (currentCachedResult: CachedResult<Customer[]> | undefined) => {
            // Safe check: get existing list or empty array
            const currentCustomerList = currentCachedResult?.data || [];
            // Optimistic update pattern: append new item to existing list
            return {
                data: [...currentCustomerList, newCustomer],
                lastUpdated: Date.now() 
            };
        }, 
        { revalidate: false }
    );
    
    return newCustomer;
  };

  // Entire list refresh
  const refreshMainList = () => {
    return globalMutate(SWR_KEYS.CUSTOMERS); 
  };

  return {
    customers: customerData?.data || [],
    lastUpdated: customerData?.lastUpdated || undefined,
    isLoading,
    isError: error,
    createCustomer,
    refreshMainList
  };
}